/**
 * RoleManagementPanel — assign/revoke V2 §23 roles.
 * Roles: moderator, finance, legal, event_coordinator,
 *        community_moderator, cs_rep, mentor, admin.
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useFounderAdmin } from "@/lib/founder/useFounderAdmin";
import { toast } from "@/hooks/use-toast";

const ROLES = [
  "moderator","finance","legal","event_coordinator",
  "community_moderator","cs_rep","mentor","admin",
];

interface Row {
  user_id: string;
  role: string;
  app_id: string | null;
  username: string | null;
}

export const RoleManagementPanel = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [target, setTarget] = useState("");
  const [role, setRole] = useState("moderator");
  const { run, busy } = useFounderAdmin();

  const reload = async () => {
    const { data } = await supabase
      .from("user_roles")
      .select("user_id, role, app_id, profiles!inner(username)")
      .order("created_at", { ascending: false })
      .limit(200);
    const flat = (data ?? []).map((r) => {
      const profilesField = (r as { profiles?: unknown }).profiles;
      const username = Array.isArray(profilesField)
        ? (profilesField[0] as { username?: string | null } | undefined)?.username ?? null
        : ((profilesField as { username?: string | null } | null)?.username ?? null);
      return {
        user_id: (r as { user_id: string }).user_id,
        role: (r as { role: string }).role,
        app_id: (r as { app_id: string | null }).app_id,
        username,
      } satisfies Row;
    });
    setRows(flat);
  };

  useEffect(() => { void reload(); }, []);

  const assign = async () => {
    if (!target.trim()) return;
    const res = await run({
      kind: "assign_role",
      target_user_id: target.trim(),
      payload: { role },
    });
    if (res.ok) {
      toast({ title: "Role assigned", description: `${role} → ${target.slice(0,8)}…` });
      setTarget("");
      await reload();
    } else {
      toast({ title: "Failed", description: res.error ?? "", variant: "destructive" });
    }
  };

  const revoke = async (user_id: string, r: string) => {
    const res = await run({ kind: "revoke_role", target_user_id: user_id, payload: { role: r } });
    if (res.ok) { toast({ title: "Revoked" }); await reload(); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <div className="text-sm font-medium">Assign role</div>
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="Target user UUID"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="flex-1 min-w-[260px]"
          />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={assign} disabled={busy || !target.trim()}>Assign</Button>
        </div>
      </Card>

      <div className="space-y-2">
        <div className="text-sm font-medium">Active assignments</div>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No roles assigned yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {rows.map((r, i) => (
              <li key={`${r.user_id}-${r.role}-${i}`}>
                <Card className="p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm truncate">
                      <span className="font-medium">{r.username ?? "(no username)"}</span>
                      <span className="text-muted-foreground font-mono text-xs ml-2">
                        {r.user_id.slice(0, 8)}…
                      </span>
                    </div>
                    <div className="mt-1"><Badge variant="secondary">{r.role}</Badge></div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => revoke(r.user_id, r.role)}>
                    Revoke
                  </Button>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default RoleManagementPanel;
